import React from "react";
import { Row, Col, Badge } from "react-bootstrap";
import { Modal, ModalHeader, ModalBody } from "reactstrap";
import { withTranslation } from "react-i18next";
import { formatINR, formatDisplayDateOrDash } from "../../helpers";
import { DATE_FORMAT } from "../../helpers/dateFormat";

const TransactionDetailModal = ({ show, toggle, transaction, t }) => {
    if (!transaction) return null;

    const formatTransactionDate = (date) => {
        try {
            return formatDisplayDateOrDash(date, DATE_FORMAT.DISPLAY_WITH_TIME);
        } catch {
            return "—";
        }
    };

    const isDebit = transaction.isDebit;
    const typeColor = transaction.transactionType === "deposit"
        ? "success"
        : transaction.transactionType === "withdraw"
            ? "danger"
            : transaction.transactionType === "transfer"
                ? "info"
                : "secondary";

    const detailFields = [
        {
            label: t("account.transactionDetail.transactionType"),
            value: (
                <Badge bg={typeColor} className="text-uppercase px-2 py-1" style={{ fontSize: "12px" }}>
                    {transaction.transactionType}
                </Badge>
            ),
        },
        {
            label: t("account.transactionDetail.amount"),
            value: (
                <span className={`fw-bold fs-5 ${isDebit ? "text-danger" : "text-success"}`}>
                    {isDebit ? "−" : "+"} {formatINR(transaction.amount)}
                </span>
            ),
        },
        {
            label: t("account.transactionDetail.type"),
            value: (
                <Badge bg={isDebit ? "danger" : "success"} pill className="px-3 py-1">
                    {isDebit ? t("account.transactionDetail.debit") : t("account.transactionDetail.credit")}
                </Badge>
            ),
        },
        {
            label: t("account.transactionDetail.balanceBefore"),
            value: <span className="fw-semibold">{formatINR(transaction.balanceBeforeTransaction)}</span>,
        },
        {
            label: t("account.transactionDetail.transactionDate"),
            value: formatTransactionDate(transaction.transactionDate),
        },
    ];

    // Conditionally add optional fields
    if (transaction.description) {
        detailFields.push({
            label: t("account.transactionDetail.remarks"),
            value: transaction.description,
        });
    }

    if (transaction.referenceNumber) {
        detailFields.push({
            label: t("account.transactionDetail.referenceNo"),
            value: <span className="font-monospace">{transaction.referenceNumber}</span>,
        });
    }

    if (transaction.fromAccount || transaction.fromAccountName) {
        detailFields.push({
            label: t("account.transactionDetail.fromAccount"),
            value: transaction.fromAccountName || transaction.fromAccount,
        });
    }

    if (transaction.toAccount || transaction.toAccountName) {
        detailFields.push({
            label: t("account.transactionDetail.toAccount"),
            value: transaction.toAccountName || transaction.toAccount,
        });
    }

    if (transaction.createdAt) {
        detailFields.push({
            label: t("account.transactionDetail.createdAt"),
            value: formatTransactionDate(transaction.createdAt),
        });
    }

    return (
        <Modal isOpen={show} toggle={toggle} centered size="md">
            <ModalHeader toggle={toggle} className="border-bottom">
                <div className="d-flex align-items-center gap-2">
                    <i className={`bx ${isDebit ? "bx-trending-down text-danger" : "bx-trending-up text-success"} fs-4`}></i>
                    <span>{t("account.transactionDetail.title")}</span>
                </div>
            </ModalHeader>
            <ModalBody className="p-0">
                {/* Amount highlight */}
                <div
                    className="text-center py-4"
                    style={{
                        background: isDebit
                            ? "linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)"
                            : "linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)",
                    }}
                >
                    <p className="mb-1 text-muted small text-uppercase" style={{ letterSpacing: "1px" }}>
                        {t("account.transactionDetail.amount")}
                    </p>
                    <h2 className={`mb-0 fw-bold ${isDebit ? "text-danger" : "text-success"}`}>
                        {isDebit ? "−" : "+"} {formatINR(transaction.amount)}
                    </h2>
                    <Badge bg={typeColor} className="text-uppercase mt-2 px-3 py-1" style={{ fontSize: "11px" }}>
                        {transaction.transactionType}
                    </Badge>
                </div>

                {/* Details list */}
                <div className="p-4">
                    {detailFields.map((field, index) => (
                        <Row
                            key={index}
                            className={`py-3 align-items-center ${index < detailFields.length - 1 ? "border-bottom" : ""}`}
                        >
                            <Col xs={5}>
                                <span className="text-muted small">{field.label}</span>
                            </Col>
                            <Col xs={7} className="text-end">
                                {field.value}
                            </Col>
                        </Row>
                    ))}
                </div>
            </ModalBody>
        </Modal>
    );
};

export default withTranslation()(TransactionDetailModal);
